import type { Entry } from "../types";

export const supportMessages = {
  navSupport: { ko: "문의 내역", en: "Support" },
  navSupportInbox: { ko: "고객 문의함", en: "Support Inbox" },

  // Reporting dialog
  reportProblem: { ko: "문제 신고", en: "Report a problem" },
  reportProblemTitle: { ko: "문제를 신고합니다", en: "Report a problem" },
  reportProblemHelp: {
    ko: "이 화면의 정보는 자동으로 첨부됩니다. 무엇을 기대했고 실제로 어떻게 되었는지만 알려주세요.",
    en: "The context of this screen is attached automatically. Just tell us what you expected and what happened instead.",
  },
  reportFinding: { ko: "이 지적 사항이 잘못되었나요?", en: "This finding looks wrong" },
  supportKind: { ko: "문의 유형", en: "What kind of problem?" },
  supportSubject: { ko: "제목", en: "Subject" },
  supportExpected: { ko: "기대한 결과", en: "What you expected" },
  supportExpectedPlaceholder: {
    ko: "예: 이 문구는 승인 문구라 지적되지 않아야 합니다.",
    en: "e.g. This wording is approved and should not have been flagged.",
  },
  supportObserved: { ko: "실제 결과", en: "What happened instead" },
  supportObservedPlaceholder: {
    ko: "예: 위험 등급이 '검토 필요'로 표시되었습니다.",
    en: "e.g. It was flagged as Review Required.",
  },
  supportSeverityLabel: { ko: "업무 영향", en: "Impact on your work" },
  supportSubmit: { ko: "신고 보내기", en: "Send report" },
  supportSubmitted: { ko: "신고가 접수되었습니다", en: "Your report has been received" },
  supportSubmittedHelp: {
    ko: "접수 번호 {reference}. 이 화면의 진단 정보가 함께 전달되어 추가 질문 없이 확인할 수 있습니다.",
    en: "Reference {reference}. The diagnostic context of this screen went with it, so we can look into it without asking follow-up questions.",
  },
  supportAttachedContext: { ko: "자동 첨부되는 정보", en: "Attached automatically" },
  supportAttachedContextHelp: {
    ko: "검토 상태, 처리 단계, 오류 메시지, 적용된 기준 세트가 함께 전달됩니다. 광고 문안이나 첨부 파일 내용은 전달되지 않습니다.",
    en: "Review status, pipeline stage, error message, and the frozen source set are sent with the report. Advertising copy and file contents are not.",
  },
  errSupportSubmit: { ko: "신고를 보내지 못했습니다.", en: "Could not send the report." },

  // Anchored result reporting: the reporter clicks next to the AI result they
  // want inspected and picks a cause, so no prose is required.
  reportThisResult: { ko: "이 결과 신고", en: "Report this result" },
  reportThisRow: { ko: "이 항목 신고", en: "Report this item" },
  reportAnalysisFailure: { ko: "분석 실패 신고", en: "Report this failure" },
  reportResultTitle: { ko: "이 결과를 검토 요청합니다", en: "Ask us to inspect this result" },
  reportResultHelp: {
    ko: "무엇이 잘못되었는지 하나만 골라 주세요. 어떤 결과인지, 어떤 기준과 실행에서 나왔는지는 자동으로 첨부됩니다.",
    en: "Pick what looks wrong. Which result it is, and which sources and run produced it, are attached for you.",
  },
  reportResultReasonLabel: { ko: "무엇이 잘못되었나요?", en: "What looks wrong?" },
  reportResultNoteLabel: { ko: "덧붙일 내용 (선택)", en: "Anything to add? (optional)" },
  reportResultNotePlaceholder: {
    ko: "비워 두어도 접수됩니다.",
    en: "You can leave this empty.",
  },
  reportSubjectAiSummary: { ko: "AI 요약: {title}", en: "AI summary: {title}" },
  reportSubjectAnalysisFailure: { ko: "분석 실패: {title}", en: "Analysis failed: {title}" },
  analysisFailedNotice: {
    ko: "이 자료의 분석이 결과를 만들지 못했습니다. 신고하면 실패 시점의 진단 정보가 함께 전달됩니다.",
    en: "The analysis of this material never produced a result. Reporting it sends the diagnostics captured at the point of failure.",
  },
  supportAttachedContextAt: {
    ko: "지금 확인한 첨부 정보",
    en: "Attached automatically, read just now",
  },
  supportAttachedContextLoading: { ko: "첨부 정보를 확인하고 있습니다…", en: "Reading the context…" },

  // Reason on the ticket list and thread
  supportColReason: { ko: "사유", en: "Reason" },
  supportInboxAllReasons: { ko: "모든 사유", en: "All reasons" },
  diagChecklistItem: { ko: "대상 점검 항목", en: "Disputed checklist item" },
  diagChecklistVerdict: { ko: "점검 결과", en: "Checklist verdict" },
  diagAnchor: { ko: "신고 위치", en: "Reported from" },

  // Client-side ticket list and thread
  supportListTitle: { ko: "문의 내역", en: "Support" },
  supportListHelp: {
    ko: "제품 안에서 신고한 문제와 처리 상태입니다.",
    en: "Problems reported from inside the product, and where each one stands.",
  },
  supportListHelpAdmin: {
    ko: "이 조직에서 신고된 모든 문의입니다. 다른 사람이 이미 올린 건을 여기서 확인할 수 있습니다.",
    en: "Every report raised in this organization, so you can see an incident is already filed.",
  },
  supportEmpty: { ko: "아직 신고한 문제가 없습니다.", en: "Nothing reported yet." },
  supportEmptyAdmin: {
    ko: "이 조직에는 아직 신고된 문의가 없습니다.",
    en: "No reports in this organization yet.",
  },
  supportColReference: { ko: "번호", en: "Ref" },
  supportColSubject: { ko: "제목", en: "Subject" },
  supportColKind: { ko: "유형", en: "Kind" },
  supportColStatus: { ko: "상태", en: "Status" },
  supportColSeverity: { ko: "영향", en: "Impact" },
  supportColOrganization: { ko: "조직", en: "Organization" },
  supportColAge: { ko: "경과", en: "Age" },
  supportColUpdated: { ko: "최근 업데이트", en: "Updated" },
  supportUnreadUpdateHelp: {
    ko: "아직 확인하지 않은 변경이 있습니다.",
    en: "There is a change you have not opened yet.",
  },
  supportUnreadReply: { ko: "읽지 않은 답변", en: "Unread reply" },
  supportUnreadReplyHelp: {
    ko: "아직 확인하지 않은 답변이 있습니다. 열면 표시가 사라집니다.",
    en: "There is a reply you have not opened yet. Opening the ticket clears this.",
  },
  supportReplyCount: { ko: "대화 {count}건", en: "{count} in the thread" },
  supportCloseTicket: { ko: "문의 닫기", en: "Close ticket" },
  supportCloseTicketHelp: {
    ko: "해결됨으로 표시합니다. 입력한 내용은 닫힘 기록과 함께 남습니다.",
    en: "Marks this as resolved. Anything written here is stored with the close record.",
  },
  errSupportClose: { ko: "문의를 닫지 못했습니다.", en: "Could not close the ticket." },
  supportClosedBy: {
    ko: "{email}님이 해결됨으로 닫았습니다.",
    en: "{email} closed this as resolved.",
  },
  supportClosedWontFixBy: {
    ko: "{email}님이 조치 없음으로 닫았습니다.",
    en: "{email} closed this as won't fix.",
  },
  supportThreadClosed: {
    ko: "이 문의는 닫혔습니다.",
    en: "This ticket is closed.",
  },
  supportReplyPlaceholder: { ko: "답변을 입력하세요", en: "Write a reply" },
  supportReplyOrClosePlaceholder: {
    ko: "답변을 쓰거나, 메모를 남기고 문의를 닫으세요",
    en: "Write a reply, or add a note and close the ticket",
  },
  supportSearch: { ko: "검색", en: "Search" },
  supportSearchPlaceholder: {
    ko: "접수 번호, 제목, 신고자, 내용",
    en: "Reference, subject, reporter, or what was written",
  },
  supportSortLabel: { ko: "정렬", en: "Sort by" },
  supportClearFilters: { ko: "필터 지우기", en: "Clear filters" },
  supportThread: { ko: "대화", en: "Conversation" },
  supportReply: { ko: "답변 보내기", en: "Send reply" },
  supportNoMessages: { ko: "아직 주고받은 메시지가 없습니다.", en: "No messages yet." },
  supportAutoOpened: { ko: "자동 생성", en: "Auto-opened" },
  supportAutoOpenedHelp: {
    ko: "분석 작업이 재시도를 모두 소진해 시스템이 직접 생성한 건입니다.",
    en: "The platform opened this itself after an analysis job exhausted its retries.",
  },
  supportReportedBy: { ko: "{email} 신고", en: "Reported by {email}" },
  supportOpenReview: { ko: "검토 화면 열기", en: "Open review" },
  supportOpenAudit: { ko: "감사 기록 보기", en: "Open audit trail" },
  supportBackToList: { ko: "문의 목록", en: "Back to the list" },
  supportTicketContextTitle: { ko: "함께 전달된 정보", en: "What was sent with this" },
  supportTicketContextHelp: {
    ko: "신고 시점에 서버가 함께 수집한 정보입니다. 지원팀은 이 내용을 보고 있으니 추가로 설명하지 않아도 됩니다.",
    en: "Collected by the server when you sent the report. Support is looking at this, so there is nothing more you need to describe.",
  },
  supportTicketContextNoneHelp: {
    ko: "이 신고에는 검토 화면 정보가 함께 전달되지 않았습니다. 특정 검토 결과에 대한 문의라면 해당 화면에서 다시 신고해 주세요.",
    en: "No review context went with this report. If it is about a specific result, report it again from that screen so the run and sources travel with it.",
  },
  errSupportReply: { ko: "답변을 보내지 못했습니다.", en: "Could not send the reply." },

  // Super Admin inbox
  supportInboxTitle: { ko: "고객 문의함", en: "Support Inbox" },
  supportInboxHelp: {
    ko: "모든 조직의 문의를 한 곳에서 보고 처리합니다. 각 건에는 신고 시점의 진단 정보가 함께 저장되어 있습니다.",
    en: "Every organization's tickets in one queue. Each one carries the diagnostic context captured when it was raised.",
  },
  supportInboxOpen: { ko: "미해결", en: "Open" },
  supportInboxBlocking: { ko: "업무 중단", en: "Blocking" },
  supportInboxAwaiting: { ko: "첫 응답 대기", en: "Awaiting first reply" },
  supportInboxOldest: { ko: "가장 오래된 건", en: "Oldest open" },
  supportInboxOpenOnly: { ko: "미해결만 보기", en: "Open only" },
  supportInboxAllStatuses: { ko: "모든 상태", en: "All statuses" },
  supportInboxAllKinds: { ko: "모든 유형", en: "All kinds" },
  supportInboxAllSeverities: { ko: "모든 영향도", en: "All impacts" },
  supportInboxAllOrganizations: { ko: "모든 조직", en: "All organizations" },
  supportInboxEmpty: { ko: "조건에 맞는 문의가 없습니다.", en: "No tickets match these filters." },
  supportDiagnosticsTitle: { ko: "진단 정보", en: "Diagnostics" },
  supportDiagnosticsHelp: {
    ko: "신고 시점에 서버가 수집한 값입니다. 브라우저가 보낸 값이 아닙니다.",
    en: "Collected by the server when the report was raised, not supplied by the browser.",
  },
  supportDiagnosticsNoReview: {
    ko: "이 신고는 검토 화면에 연결되어 있지 않아 수집된 진단 정보가 없습니다. 아래 신고 위치를 참고하고, 특정 결과에 대한 건이라면 해당 화면에서 다시 신고해 달라고 안내하세요.",
    en: "This report is not anchored to a review, so there was no bundle to collect. Use the page path below, and if it is about a specific result, ask the reporter to raise it from that screen.",
  },
  supportSetStatus: { ko: "상태 변경", en: "Change status" },
  supportSetSeverity: { ko: "영향도 변경", en: "Change impact" },
  supportHours: { ko: "{value}시간", en: "{value}h" },
  supportDays: { ko: "{value}일", en: "{value}d" },

  // The disputed AI result, frozen at report time
  supportDisputedTitle: { ko: "신고 시점의 AI 결과", en: "The AI result as reported" },
  supportDisputedHelp: {
    ko: "{at}에 저장된 사본입니다. 재분석하면 실제 결과는 달라질 수 있으므로, 신고 대상이 무엇이었는지는 이 사본이 유일한 기록입니다.",
    en: "Copied at {at}. A rerun replaces the live result, so this is the only record of what was actually reported.",
  },
  supportDisputedFinding: { ko: "신고된 지적 사항", en: "The disputed finding" },
  supportDisputedExplanation: { ko: "AI 설명", en: "AI explanation" },
  supportDisputedEvidence: { ko: "근거로 인용된 문구", en: "Evidence quote" },
  supportDisputedEvidenceType: { ko: "근거 유형", en: "Evidence type" },
  supportDisputedConfidence: { ko: "확신도", en: "Confidence" },
  supportDisputedPage: { ko: "페이지", en: "Page" },
  supportDisputedSuggestion: { ko: "제안된 수정안", en: "Suggested revision" },
  supportDisputedNote: { ko: "점검 메모", en: "Checklist note" },
  supportDisputedRun: { ko: "분석 결과 전체", en: "The run as a whole" },
  supportDisputedSummary: { ko: "AI 요약", en: "AI summary" },
  supportDisputedOverview: { ko: "자료 파악 내용", en: "What the AI saw in the material" },
  supportDisputedAction: { ko: "권고 조치", en: "Recommended action" },

  // Live inspection: the material the AI was given and what it produced
  supportInspectTitle: { ko: "AI가 본 자료와 결과", en: "What the AI was given, and produced" },
  supportInspectHelp: {
    ko: "고객사의 원본 자료를 조회합니다. 열람 기록은 해당 조직의 감사 기록에 남습니다.",
    en: "Reads the client's own material. Every open is recorded in that organization's audit trail.",
  },
  supportInspectOpen: { ko: "자료와 결과 열람", en: "Open the material and result" },
  supportInspectOpenFile: { ko: "원본 파일 보기", en: "Open the original file" },
  supportInspectMaterial: { ko: "제출 자료", en: "Submitted material" },
  supportInspectBodyText: {
    ko: "분석에 사용된 추출 텍스트",
    en: "Extracted text the analysis actually ran on",
  },
  supportInspectNoBodyText: {
    ko: "추출된 텍스트가 없습니다. 텍스트 기반 검사는 아무것도 검사하지 못했다는 뜻이며, 이 자체가 원인일 수 있습니다.",
    en: "No extracted text. The text-based checks had nothing to run on, which may itself be the cause.",
  },
  supportInspectTruncated: {
    ko: "길어서 일부만 표시합니다. 전체는 원본 파일에서 확인하세요.",
    en: "Truncated. Open the original file for the whole thing.",
  },
  supportInspectResult: { ko: "분석 결과", en: "Analysis result" },
  supportInspectChecklist: { ko: "점검 항목별 판정", en: "Per-reference verdicts" },
  supportInspectFindings: { ko: "지적 사항 {count}건", en: "Findings ({count})" },
  supportInspectSources: {
    ko: "이 분석에 적용된 기준 {count}건",
    en: "Sources frozen for this run ({count})",
  },
  supportInspectDisputedBadge: { ko: "신고 대상", en: "Disputed" },
  supportInspectFindingGone: {
    ko: "신고된 지적 사항이 현재 분석 결과에 없습니다. 재분석으로 교체된 것으로 보이며, 위의 신고 시점 사본이 유일한 기록입니다.",
    en: "The reported finding is no longer in the run — a rerun replaced it. The frozen copy above is the only record of it.",
  },
  supportInspectStaleRevision: {
    ko: "신고 이후 새 개정본이 제출되었습니다. 아래 내용은 신고 대상 개정본이며, 현재 검토 화면과 다를 수 있습니다.",
    en: "A newer revision was submitted after this report. What follows is the reported revision, not what the review shows now.",
  },
  supportOpenReviewInOrg: { ko: "{name} 조직의 검토를 새 탭에서 열기", en: "Open the {name} review in a new tab" },
  errSupportInspect: { ko: "자료를 불러오지 못했습니다.", en: "Could not load the material." },

  // Diagnostic field labels
  diagReview: { ko: "검토", en: "Review" },
  diagReviewStatus: { ko: "검토 상태", en: "Review status" },
  diagRisk: { ko: "위험 등급", en: "Risk" },
  diagRouting: { ko: "라우팅 결과", en: "Routing outcome" },
  diagRoutingReason: { ko: "라우팅 사유", en: "Routing reason" },
  diagChannel: { ko: "매체", en: "Channel" },
  diagCategory: { ko: "자료 구분", en: "Category" },
  diagSubmittedAt: { ko: "제출 시각", en: "Submitted" },
  diagRevision: { ko: "리비전", en: "Revision" },
  diagProcessing: { ko: "자료 처리 상태", en: "Material processing" },
  diagFileName: { ko: "파일", en: "File" },
  diagProgressStage: { ko: "처리 단계", en: "Pipeline stage" },
  diagJobStatus: { ko: "작업 상태", en: "Job status" },
  diagAttempts: { ko: "시도 횟수", en: "Attempts" },
  diagLastError: { ko: "마지막 오류", en: "Last error" },
  diagFingerprint: { ko: "기준 세트 지문", en: "Source set fingerprint" },
  diagSourceCount: { ko: "적용 기준 수", en: "Applicable sources" },
  diagAnalysisRun: { ko: "분석 실행", en: "Analysis run" },
  diagAnalysisStatus: { ko: "분석 상태", en: "Analysis status" },
  diagAnalysisError: { ko: "분석 오류", en: "Analysis error" },
  diagSchemaValid: { ko: "AI 응답 검증", en: "AI response validated" },
  diagRejectedFindings: { ko: "폐기된 지적 사항", en: "Discarded findings" },
  diagFindingCount: { ko: "지적 사항 수", en: "Findings" },
  diagFinding: { ko: "대상 지적 사항", en: "Disputed finding" },
  diagFindingSeverity: { ko: "심각도", en: "Severity" },
  diagDetectionMethod: { ko: "탐지 방식", en: "Detection method" },
  diagCitation: { ko: "인용 근거", en: "Citation" },
  diagRuleVersion: { ko: "규칙 버전", en: "Rule version" },
  diagPolicyVersion: { ko: "정책 버전", en: "Policy version" },
  diagPagePath: { ko: "화면 경로", en: "Page" },
  diagReporterRole: { ko: "신고자 역할", en: "Reporter role" },
  diagAppVersion: { ko: "앱 버전", en: "App version" },
  diagCollectedAt: { ko: "수집 시각", en: "Collected" },
} as const satisfies Record<string, Entry>;
